interface BrazeConfig {
  braze_enabled: boolean
  braze_sdk_endpoint: string
  braze_web_sdk_api_key: string
}

// A thin wrapper around the shared `braze-tc` library's `brazeInitialise()`,
// gated on `Config.braze_enabled` and a Web SDK API key.
//
// Consent gating (Didomi) is handled internally by `brazeInitialise()`, so
// calling this unconditionally when the SDK is enabled is safe - it won't
// actually initialise Braze until the reader has consented, or is in a
// region where consent isn't required.
//
// `@braze/web-sdk` is large, so `brazeInitialise` is dynamically imported
// here rather than statically at the top of this file. That keeps the SDK
// out of the `application.js` bundle, and readers only download it when
// Braze can start.
//
// `Config.braze_enabled` also gates the server-side Braze jobs, so it can be
// on while the Web SDK API key is blank. The SDK refuses to start without a
// key, so we don't download it at all in that case.
export default class Braze {
  static async start(config: BrazeConfig): Promise<void> {
    if (!config.braze_enabled || !config.braze_web_sdk_api_key) {
      return
    }

    // On a slow connection the SDK chunk can time out. Braze isn't part of
    // the donation flow, so we skip it rather than report an error we can't
    // act on. Any other error still reaches Bugsnag.
    const brazeTc = await import("@theconversation/shared-libraries/braze-tc").catch(
      (error: Error): null => {
        if (error.name === "ChunkLoadError") {
          return null
        }

        throw error
      },
    )

    if (!brazeTc) {
      return
    }

    brazeTc.brazeInitialise(config.braze_sdk_endpoint, config.braze_web_sdk_api_key)
  }
}
